import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import { HttpExceptionCode } from '@thesavingsgroup/http/errors/HttpExceptionCode';
import i18n from 'i18next';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { DeliverPinLocation } from '../pinStore/enums/DeliverPinLocation';
import { DeliverPinParams } from '../pinStore/types/DeliverPinParams';
import { AuthStatus } from './enums/AuthStatus';
import { SessionCheckTrigger } from './enums/SessionCheckTrigger';
import { UserExistsExceptionCode } from './enums/UserExistsExceptionCode';
import { User } from './types/User';
import { UserState } from './types/UserState';
import { UserStateDependencies } from './types/UserStateDependencies';

export const keysToPersist = ['pinPurpose'];

let bootstrapSessionInFlight: Promise<void> | null = null;

export const userStore =
  ({
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useLeadChannelStore,
    useParameterStore,
    useRouteStore,
  }: UserStateDependencies): StateCreator<UserState> =>
  (set, get) => ({
    authStatus: AuthStatus.PENDING,
    fetchCurrentUserFailed: false,
    isUpdatingUser: false,
    pinPurpose: null,
    smsOptIn: false,
    user: null,
    userAuthenticateFailedMessage: null,
    userAuthenticateLocked: false,
    userAuthenticateSuccess: false,
    userCreateFailedMessage: null,
    userCreateSuccess: false,
    userExistsException: null,
    userSignedUp: false,
    userUpdateFailedMessage: null,
    userUpdateSuccess: false,

    computed: {
      get hasCurrentUser(): boolean {
        return (
          get().authStatus === AuthStatus.AUTHENTICATED && !isEmpty(get().user)
        );
      },
      get isAuthenticated(): boolean {
        return get().authStatus === AuthStatus.AUTHENTICATED;
      },
      get isPinPurposeEmpty(): boolean {
        return isEmpty(get().pinPurpose);
      },
      get shouldCreateUser(): boolean {
        return get().pinPurpose === AuthType.ACCOUNT_REGISTRATION_USING_PIN;
      },
      get shouldUpdatePhone(): boolean {
        return get().pinPurpose === AuthType.EDIT_PHONE_USING_PIN;
      },
      get userEmailAddress(): string {
        const user = get().user;
        const { primaryEmailAddress = '' } = user || {};

        return primaryEmailAddress;
      },
      get userFirstName(): string {
        const user = get().user;
        const { firstName = '' } = user || {};

        return firstName;
      },
      get userLastName(): string {
        const user = get().user;
        const { lastName = '' } = user || {};

        return lastName;
      },
      get userPhoneNumber(): string {
        const user = get().user;
        const { primaryPhoneNumber = '' } = user || {};

        return primaryPhoneNumber;
      },
      get userTruncatedName(): string {
        const user = get().user;
        const { firstName = '', lastName = '' } = user || {};

        return `${firstName} ${lastName.charAt(0)}.`;
      },
    },

    bootstrapSession: (trigger?: SessionCheckTrigger): Promise<void> => {
      const { authStatus } = get();

      // PENDING means we haven't checked the session yet - AUTHENTICATED/
      // UNAUTHENTICATED means we already have. Only a full page load resets
      // authStatus back to PENDING, so skip re-checking here instead of
      // re-firing this request on every route change.
      if (authStatus !== AuthStatus.PENDING) {
        return Promise.resolve();
      }

      if (!bootstrapSessionInFlight) {
        bootstrapSessionInFlight = ((): Promise<void> => {
          const portalHttpRequest = useApiStore.getState().portalApi();

          return portalHttpRequest
            .get<User>('/2.0/users/current', {
              headers: {
                // records which code path triggered this check, for log tracing
                'x-tsg-session-check-trigger': trigger ?? 'unknown',
              },
              // A 401 during bootstrap means the user is not signed in; avoid a sign-out loop.
              suppressUnauthorizedRedirect: true,
            })
            .then((user): void => {
              set({
                authStatus: AuthStatus.AUTHENTICATED,
                fetchCurrentUserFailed: false,
                user,
              });
            })
            .catch((): void => {
              set({
                authStatus: AuthStatus.UNAUTHENTICATED,
                fetchCurrentUserFailed: true,
                user: null,
              });
            });
        })().finally((): void => {
          bootstrapSessionInFlight = null;
        });
      }

      return bootstrapSessionInFlight;
    },
    clearPinPurpose: (): void => {
      set({ pinPurpose: null });
    },
    clearPinRetryState: (): void => {
      set({
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
        userCreateFailedMessage: null,
      });
    },
    clearSession: (): void => {
      set({
        authStatus: AuthStatus.UNAUTHENTICATED,
        user: null,
      });
    },
    clearUserSuccessState: (): void => {
      set({
        userAuthenticateSuccess: false,
        userCreateSuccess: false,
        userSignedUp: false,
        userUpdateSuccess: false,
      });
    },
    clearUserUpdate: (): void => {
      set({
        isUpdatingUser: false,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });
    },
    deriveDeliverPinParams(
      userExistsException: UserExistsExceptionCode,
      deliverPinLocation: DeliverPinLocation,
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): DeliverPinParams {
      /*
        Determines the auth intent from the userExistsException, persists it,
        and builds the params needed to deliver the PIN:
        - BOTH_NOT_FOUND            - create a new account
        - EMAIL_ADDRESS_REGISTERED  - update the phone number on an existing account
        - anything else             - sign in to an existing account

        For more information, see: https://savingsgroup.atlassian.net/wiki/x/AQBvRQ
      */
      const { getDeliverPinParams } = get();

      const pinPurpose =
        userExistsException === UserExistsExceptionCode.BOTH_NOT_FOUND
          ? AuthType.ACCOUNT_REGISTRATION_USING_PIN
          : userExistsException ===
            UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED
          ? AuthType.EDIT_PHONE_USING_PIN
          : AuthType.ACCOUNT_SIGN_IN_USING_PIN;

      set({ pinPurpose });

      return getDeliverPinParams(
        deliverPinLocation,
        primaryPhoneNumber,
        primaryEmailAddress,
      );
    },
    async ensureSessionForRouteLoader(): Promise<Response | null> {
      await get().bootstrapSession(SessionCheckTrigger.ROUTE_LOADER);

      if (get().authStatus === AuthStatus.UNAUTHENTICATED) {
        const {
          computed: { nonAuthRedirectPath },
        } = useRouteStore.getState();

        const redirectUrl = new URL(
          nonAuthRedirectPath,
          window.location.origin,
        );

        return Response.redirect(redirectUrl.toString(), HttpStatusCode.FOUND);
      }

      return null;
    },
    fetchCurrentUser: (): Promise<void> => {
      return get().bootstrapSession(SessionCheckTrigger.FETCH_CURRENT_USER);
    },

    getDeliverPinParams(
      deliverPinLocation: DeliverPinLocation,
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): DeliverPinParams {
      const {
        computed: { shouldUpdatePhone },
        pinPurpose,
      } = get();

      return {
        // When updating phone number, we should provide the email
        // as it's needed in the api to identify the existing user account.
        ...(shouldUpdatePhone && { email: primaryEmailAddress }),
        deliverPinLocation,
        phone: primaryPhoneNumber,
        purpose: pinPurpose,
      };
    },
    setSmsOptIn: (value: boolean): void => {
      set({ smsOptIn: value });
    },
    setUserFirstName: (value: string): void => {
      set({ user: { ...get().user, firstName: value } });
    },
    setUserLastName: (value: string): void => {
      set({ user: { ...get().user, lastName: value } });
    },
    setUserSignedUp: (value: boolean): void => {
      set({ userSignedUp: value });
    },
    userAgreeToTerms: (vehicleId: string | null | undefined): Promise<void> => {
      if (!vehicleId) {
        return Promise.resolve();
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest.post('/2.0/accepted-terms-of-use', {
        vehicleId,
      });
    },
    userAuthenticate: (
      primaryPhoneNumber: string,
      pin: string,
      leadChannelCode: string | null | undefined,
      purpose: AuthType,
      agreeToTermsForVehicleId?: string | null,
    ): Promise<void> => {
      set({
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      const { vehicleId } = useParameterStore.getState();
      const {
        sendUserActivatedAccountAnalytics,
        sendUserActivatedApplicationAnalytics,
        sendUserAuthenticatedAnalytics,
      } = useAnalyticStore.getState();

      return (
        portalHttpRequest
          .post<{ user: User }>(
            '/2.0/session',
            {
              attributions: attributionsForLeadChannel,
              includeUser: true,
              leadChannelCode,
              pin,
              primaryPhoneNumber,
              purpose,
              vehicleId,
            },
            {
              // we skip the sign-out redirect logic found in apiStore when a user tries to
              // authenticate because if the authentication fails with a 401, we want to show
              // an error message instead of redirecting them.
              suppressUnauthorizedRedirect: true,
            },
          )
          // We agree to terms here, if necessary, because `SessionGuard`
          // redirects as soon as `authStatus` flips, and the dashboard
          // application page would then read an application that is not marked
          // complete yet and show the applicant task instead of the
          // verification task.
          .then(
            ({ user }): Promise<User> =>
              get()
                .userAgreeToTerms(agreeToTermsForVehicleId)
                // A failed agreement must not fail authentication: the session is
                // already valid, and the catch below would mark the user
                // UNAUTHENTICATED and surface a sign-in error for it.
                .catch((error: unknown): void => {
                  console.error(error);
                })
                .then((): User => user),
          )
          .then((user): void => {
            if (purpose === AuthType.ACCOUNT_ACTIVATION_USING_PIN) {
              sendUserActivatedAccountAnalytics();
            }

            if (purpose === AuthType.APPLICATION_ACTIVATION_USING_PIN) {
              sendUserActivatedApplicationAnalytics();
            }

            if (purpose === AuthType.ACCOUNT_SIGN_IN_USING_PIN) {
              sendUserAuthenticatedAnalytics();
            }

            set({
              authStatus: AuthStatus.AUTHENTICATED,
              fetchCurrentUserFailed: false,
              user,
              userAuthenticateSuccess: true,
            });
          })
          .catch((error: unknown): void => {
            const errorCode: string | undefined = lodashGet(
              error as any,
              'response.data._embedded.errors.0.errorCode',
            );

            // CP-6949: a 403 usually means too many failed attempts, but it is
            // also returned when the account could not be proven to belong to
            // this person. That is not a lockout, so do not report it as one.
            const isAccountOwnershipUnverified =
              errorCode === HttpExceptionCode.ACCOUNT_OWNERSHIP_UNVERIFIED;

            const statusMessage = lodashGet(
              i18n.t('errors.userAuthenticate.statusMessage'),
              toString(lodashGet(error as any, 'response.status')),
              i18n.t('errors.userAuthenticate.defaultMessage'),
            );

            // prefer a message for the error code (ex: the account could not be
            // proven to be theirs, or was already activated) over the status
            const userAuthenticateFailedMessage =
              errorCode === undefined
                ? statusMessage
                : lodashGet(
                    i18n.t('errors.userAuthenticate.errorCode'),
                    toString(errorCode),
                    statusMessage,
                  );

            set({
              authStatus: AuthStatus.UNAUTHENTICATED,
              userAuthenticateFailedMessage,
              userAuthenticateLocked:
                !isAccountOwnershipUnverified &&
                lodashGet(error as any, 'response.status') ===
                  HttpStatusCode.FORBIDDEN,
            });
          })
      );
    },
    userCreate: (
      firstName: string,
      lastName: string,
      primaryEmailAddress: string,
      primaryPhoneNumber: string,
      pin: string,
    ): Promise<void> => {
      set({
        userCreateFailedMessage: null,
        userCreateSuccess: false,
        userExistsException: null,
      });

      const { smsOptIn } = get();

      const {
        computed: { isValidLeadChannel },
      } = useLeadChannelStore.getState();

      const { leadChannelCode } = useParameterStore.getState();
      const { sendUserRegisteredAnalytics } = useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      return portalHttpRequest
        .post<{ user: User }>(
          '/2.0/users',
          {
            attributions: attributionsForLeadChannel,
            firstName,
            lastName,
            ...(isValidLeadChannel && { leadChannelCode }),
            pin,
            primaryEmailAddress,
            primaryPhoneNumber,
            smsOptIn,
          },
          {
            // we skip the sign-out redirect logic found in apiStore when a user tries to
            // create an account because if the create fails with a 401, we want to show
            // an error message instead of redirecting them.
            suppressUnauthorizedRedirect: true,
          },
        )
        .then(({ user }): void => {
          sendUserRegisteredAnalytics({
            sms_opt_in_consented: smsOptIn,
          });

          set({
            authStatus: AuthStatus.AUTHENTICATED,
            fetchCurrentUserFailed: false,
            user,
            userCreateSuccess: true,
          });
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              authStatus: AuthStatus.UNAUTHENTICATED,
              userCreateFailedMessage: lodashGet(
                i18n.t('errors.userCreate.statusMessage'),
                toString(lodashGet(error as any, 'response.status')),
                i18n.t('errors.userCreate.defaultMessage'),
              ),
            });
          }
        });
    },
    userExists: (
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): Promise<void> => {
      set({ userExistsException: null });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get(`/2.0/users`, {
          params: {
            primaryEmailAddress,
            primaryPhoneNumber,
          },
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          set({ userExistsException: errorCode });
        });
    },
    userUpdate: (): Promise<void> => {
      const {
        computed: { userFirstName: firstName, userLastName: lastName },
      } = get();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        isUpdatingUser: true,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });

      return portalHttpRequest
        .patch<User>('/2.0/users/current', {
          firstName,
          lastName,
        })
        .then((user): void => {
          set({ user, userUpdateSuccess: true });
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              userUpdateFailedMessage: i18n.t(
                'errors.userUpdate.defaultMessage',
              ),
            });
          }
        })
        .finally((): void => {
          set({ isUpdatingUser: false });
        });
    },
  });
